import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import { Loader2 } from "lucide-react";
import { NoOrganizationState } from "@/components/NoOrganizationState";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

// P2 OPTIMIZATION: Combined query for partner and org status (eliminates waterfall)
async function fetchUserAccessStatus(userId: string) {
  // Run both queries in parallel with Promise.all
  const [partnerResult, orgResult] = await Promise.all([
    supabase
      .from("partner_members")
      .select("role")
      .eq("user_id", userId)
      .maybeSingle(),
    supabase
      .from("org_members")
      .select("org_id, orgs!inner(is_active)")
      .eq("user_id", userId)
  ]);

  const isPartner = partnerResult.data?.role === "owner" || partnerResult.data?.role === "admin";
  
  const memberships = orgResult.data || [];
  const hasMemberships = memberships.length > 0;
  const hasActiveOrg = hasMemberships 
    ? memberships.some((m: any) => m.orgs?.is_active !== false)
    : true;

  return {
    isPartner,
    hasMemberships,
    hasActiveOrg
  };
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading: authLoading, signOut } = useAuth();
  const { hasNoOrganization, isLoading: orgLoading } = useOrganization();
  const location = useLocation();
  const [isCheckingOrgStatus, setIsCheckingOrgStatus] = useState(false);

  // P2 OPTIMIZATION: Single combined query instead of 2 sequential queries
  const { data: accessStatus, isLoading: accessStatusLoading } = useQuery({
    queryKey: ["user-access-status", user?.id],
    queryFn: () => fetchUserAccessStatus(user!.id),
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 min cache
  });

  // Efeito para deslogar usuário de org desativada
  useEffect(() => {
    const checkAndSignOut = async () => {
      if (
        accessStatus &&
        !accessStatus.isPartner && 
        accessStatus.hasMemberships && 
        !accessStatus.hasActiveOrg &&
        !isCheckingOrgStatus
      ) {
        setIsCheckingOrgStatus(true);
        await signOut();
        window.location.href = "/auth?reason=org_inactive";
      }
    };
    
    checkAndSignOut();
  }, [accessStatus, signOut, isCheckingOrgStatus]);

  // Estado de carregamento global
  const isGlobalLoading = authLoading || (user && (orgLoading || accessStatusLoading));

  // Aguardar carregamento completo antes de tomar qualquer decisão
  if (isGlobalLoading || isCheckingOrgStatus) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Carregando...</p>
      </div>
    );
  }

  // Redirecionar para login se não autenticado
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Se usuário é partner, não verificar org
  if (accessStatus?.isPartner) {
    return <>{children}</>;
  }

  // Se org está desativada, redirecionar (fallback, useEffect deve pegar primeiro)
  if (accessStatus?.hasMemberships && !accessStatus?.hasActiveOrg) {
    return <Navigate to="/auth?reason=org_inactive" replace />;
  }

  // Exibir tela de "sem organização" SOMENTE após carregamento completo
  if (hasNoOrganization) {
    return <NoOrganizationState />;
  }

  return <>{children}</>;
}
